import {
    getColorThemeManifest,
    getFileIconThemeManifest,
    getProductIconThemeManifest,
    getProfileManifest,
} from "../../../theme/runtime/theme-resolver";
import type { SettingsTextResolver } from "./localization";

export function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}

export function formatValue(
    value: unknown,
    resolveText?: SettingsTextResolver,
): string {
    if (value === undefined) {
        return resolveSettingsText(
            "%editor.settings.value.unset%",
            "Unset",
            resolveText,
        );
    }
    if (value === null) return "null";
    if (typeof value === "string") return value.length > 0 ? value : '""';
    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

export function formatDetailedValue(
    value: unknown,
    explicitLayer = false,
    resolveText?: SettingsTextResolver,
): string {
    if (value === undefined && explicitLayer) {
        return resolveSettingsText(
            "%editor.settings.value.unset%",
            "Unset",
            resolveText,
        );
    }
    if (Array.isArray(value) || isPlainObject(value)) {
        try {
            return JSON.stringify(value, null, 2);
        } catch {
            return String(value);
        }
    }
    return formatValue(value, resolveText);
}

export function formatRawJsonValue(value: unknown): string {
    if (value === undefined) {
        return "{}";
    }
    if (typeof value === "string") {
        return JSON.stringify(value);
    }
    if (typeof value === "number" || typeof value === "boolean" || value === null) {
        return JSON.stringify(value);
    }
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return "{}";
    }
}

export function summarizeListValue(
    value: unknown,
    resolveText?: SettingsTextResolver,
): string {
    if (Array.isArray(value)) {
        return value.length === 0
            ? resolveSettingsText(
                "%editor.settings.value.emptyList%",
                "Empty list",
                resolveText,
            )
            : `${value.length} ${resolveSettingsText(
                "%editor.settings.value.items%",
                "items",
                resolveText,
            )}`;
    }
    if (isPlainObject(value)) {
        const count = Object.keys(value).length;
        return count === 0
            ? resolveSettingsText(
                "%editor.settings.value.emptyObject%",
                "Empty object",
                resolveText,
            )
            : `${count} ${resolveSettingsText(
                "%editor.settings.value.items%",
                "items",
                resolveText,
            )}`;
    }
    return formatValue(value, resolveText);
}

export function parseStringList(value: string): string[] {
    const entries = value
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean);
    return [...new Set(entries)];
}

export type ThemeSelectorEntryKind =
    | "colorTheme"
    | "productIconTheme"
    | "fileIconTheme"
    | "contentProfile"
    | "presentationProfile";

export function getThemeSelectorEntryKind(
    entryKey: string,
): ThemeSelectorEntryKind | null {
    switch (entryKey) {
        case "editor.appearance.colorTheme":
            return "colorTheme";
        case "editor.appearance.productIconTheme":
            return "productIconTheme";
        case "editor.appearance.fileIconTheme":
            return "fileIconTheme";
        case "editor.preview.contentProfile":
            return "contentProfile";
        case "editor.publish.presentationProfile":
            return "presentationProfile";
        default:
            return null;
    }
}

export function isThemeSelectorEntryKey(entryKey: string): boolean {
    return getThemeSelectorEntryKind(entryKey) !== null;
}

export function isThemeRelatedEntryKey(entryKey: string): boolean {
    return (
        entryKey.startsWith("editor.appearance.") ||
        entryKey === "editor.preview.contentProfile" ||
        entryKey === "editor.publish.presentationProfile"
    );
}

export function getEnumOptionLabel(
    entryKey: string,
    value: string,
    resolveText?: SettingsTextResolver,
): string {
    if (entryKey === "editor.defaultPreviewWidth") {
        switch (value) {
            case "fill":
            case "full":
                return resolveSettingsText(
                    "%editor.settings.previewWidth.fill%",
                    "Fill",
                    resolveText,
                );
            case "wide":
                return resolveSettingsText(
                    "%editor.settings.previewWidth.wide%",
                    "Wide",
                    resolveText,
                );
            case "work":
                return resolveSettingsText(
                    "%editor.settings.previewWidth.work%",
                    "Work",
                    resolveText,
                );
            case "reading":
            case "prose":
            case "5xl":
                return resolveSettingsText(
                    "%editor.settings.previewWidth.reading%",
                    "Reading",
                    resolveText,
                );
            case "compact":
            case "3xl":
                return resolveSettingsText(
                    "%editor.settings.previewWidth.compact%",
                    "Compact",
                    resolveText,
                );
            default:
                return value;
        }
    }
    switch (getThemeSelectorEntryKind(entryKey)) {
        case "colorTheme":
            return getColorThemeManifest(value).label;
        case "productIconTheme":
            return getProductIconThemeManifest(value).label;
        case "fileIconTheme":
            return getFileIconThemeManifest(value).label;
        case "contentProfile":
            return getProfileManifest(value, "content").label;
        case "presentationProfile":
            return getProfileManifest(value, "presentation").label;
        default:
            return value;
    }
}

export function summarizeSettingValue(
    entryKey: string,
    value: unknown,
    resolveText?: SettingsTextResolver,
): string {
    if (typeof value === "string") {
        return getEnumOptionLabel(entryKey, value, resolveText);
    }
    return summarizeListValue(value, resolveText);
}

function resolveSettingsText(
    value: string,
    fallback: string,
    resolveText?: SettingsTextResolver,
): string {
    return resolveText ? resolveText(value, fallback) : fallback;
}
